import { Edge } from '@xyflow/react';
import { atom } from 'jotai';

import type { AutomationNode } from '../_components/editor/nodes/types';

export type FlowSnapshot = {
  nodes: AutomationNode[];
  edges: Edge[];
  timestamp: number;
};

const MAX_HISTORY_SIZE = 50;

// Private atoms for storing history state
const $historyPast = atom<FlowSnapshot[]>([]);
const $historyFuture = atom<FlowSnapshot[]>([]);
const $historyPresent = atom<FlowSnapshot | null>(null);

// Public read-only atoms for checking undo/redo availability
export const $canUndo = atom((get) => get($historyPast).length > 0);
export const $canRedo = atom((get) => get($historyFuture).length > 0);

/**
 * Initialize history with the initial state
 * Clears past and future history
 */
export const $initializeHistory = atom(
  null,
  (_get, set, snapshot: FlowSnapshot) => {
    set($historyPresent, snapshot);
    set($historyPast, []);
    set($historyFuture, []);
  },
);

/**
 * Push a new snapshot to history
 * Adds current state to past, clears future
 * Skips if nothing changed (compares serialized state)
 * Caps past history at MAX_HISTORY_SIZE
 */
export const $pushHistory = atom(null, (get, set, snapshot: FlowSnapshot) => {
  const present = get($historyPresent);

  // Skip if nothing changed
  if (present) {
    const presentSerialized = JSON.stringify({
      nodes: present.nodes,
      edges: present.edges,
    });
    const snapshotSerialized = JSON.stringify({
      nodes: snapshot.nodes,
      edges: snapshot.edges,
    });

    if (presentSerialized === snapshotSerialized) {
      return;
    }
  }

  // Add current present to past
  const past = get($historyPast);
  const newPast = present ? [...past, present] : past;

  // Cap history size
  const cappedPast =
    newPast.length > MAX_HISTORY_SIZE
      ? newPast.slice(newPast.length - MAX_HISTORY_SIZE)
      : newPast;

  set($historyPast, cappedPast);
  set($historyPresent, snapshot);
  set($historyFuture, []); // Clear future on new changes
});

/**
 * Undo to previous snapshot
 * Moves present to future, returns previous snapshot from past
 * Returns null if no past history available
 */
export const $undo = atom(null, (get, set): FlowSnapshot | null => {
  const past = get($historyPast);
  if (past.length === 0) {
    return null;
  }

  const present = get($historyPresent);
  const previous = past[past.length - 1];
  const newPast = past.slice(0, past.length - 1);

  set($historyPast, newPast);
  set($historyPresent, previous);

  if (present) {
    const future = get($historyFuture);
    set($historyFuture, [present, ...future]);
  }

  return previous;
});

/**
 * Redo to next snapshot
 * Moves present to past, returns next snapshot from future
 * Returns null if no future history available
 */
export const $redo = atom(null, (get, set): FlowSnapshot | null => {
  const future = get($historyFuture);
  if (future.length === 0) {
    return null;
  }

  const present = get($historyPresent);
  const next = future[0];
  const newFuture = future.slice(1);

  set($historyFuture, newFuture);
  set($historyPresent, next);

  if (present) {
    const past = get($historyPast);
    set($historyPast, [...past, present]);
  }

  return next;
});

/**
 * Reset all history
 * Clears past, present, and future
 */
export const $resetHistory = atom(null, (_get, set) => {
  set($historyPast, []);
  set($historyFuture, []);
  set($historyPresent, null);
});
